import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button, ButtonWithLoading } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';

import { Setting } from '#components/settings/UI';

import { diaLocal, diasDesde } from './copia';
import { useCopiaSeguridad } from './useCopiaSeguridad';
import type { ResultadoCopia } from './useCopiaSeguridad';

/** «Última copia: …» según los días que han pasado (`null` = nunca). */
export function TextoUltimaCopia({ dias }: { dias: number | null }) {
  const { t } = useTranslation();
  let texto: string;
  if (dias === null) {
    texto = t('Last backup: never');
  } else if (dias === 0) {
    texto = t('Last backup: today');
  } else if (dias === 1) {
    texto = t('Last backup: yesterday');
  } else {
    texto = t('Last backup: {{count}} days ago', { count: dias });
  }
  return (
    <Text data-testid="copia-ultima" style={{ fontWeight: 600 }}>
      {texto}
    </Text>
  );
}

/**
 * Ajustes → «Copia de seguridad»: hacer la copia cuando quiera, cuándo fue
 * la última y cómo se recupera. En Chrome Android abre la hoja de compartir
 * (para «Guardar en Drive»); si la cierra sin elegir, ofrece «Descargar».
 */
export function CopiaSettings() {
  const { ultima, hacerCopia, descargar } = useCopiaSeguridad('ajustes');
  const [ocupado, setOcupado] = useState(false);
  const [resultado, setResultado] = useState<ResultadoCopia | null>(null);

  const dias = diasDesde(ultima, diaLocal(new Date()));
  const ofrecerDescarga =
    resultado === 'cancelada' || resultado === 'fallo-compartir';

  const ejecutar = async (accion: () => Promise<ResultadoCopia>) => {
    setOcupado(true);
    setResultado(await accion());
    setOcupado(false);
  };

  return (
    <Setting
      primaryAction={
        <View style={{ gap: 10, width: '100%' }}>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
            <ButtonWithLoading
              variant="primary"
              data-testid="copia-ahora"
              isLoading={ocupado}
              onPress={() => void ejecutar(hacerCopia)}
              style={{ minHeight: 44 }}
            >
              <Trans>Make a backup now</Trans>
            </ButtonWithLoading>
            {ofrecerDescarga && (
              <Button
                isDisabled={ocupado}
                onPress={() => void ejecutar(descargar)}
                style={{ minHeight: 44 }}
              >
                <Trans>Download</Trans>
              </Button>
            )}
          </View>
          {ofrecerDescarga && (
            <Text style={{ fontSize: 12 }}>
              <Trans>
                The backup was not saved. You can download it to your phone
                instead.
              </Trans>
            </Text>
          )}
          {(resultado === 'compartida' || resultado === 'descargada') && (
            <Text style={{ fontSize: 12 }}>
              <Trans>Backup done.</Trans>
            </Text>
          )}
          {resultado === 'error' && (
            <Text style={{ fontSize: 12, color: theme.errorText }}>
              <Trans>The backup could not be made. Try again later.</Trans>
            </Text>
          )}
        </View>
      }
    >
      <View data-testid="copia-ajustes" style={{ gap: 8 }}>
        <Text>
          <Trans>
            <strong>Backup</strong>: a file with all your data. It is made by
            itself: choose where and how often with the anchor at the top of
            Home. Here you can also send one to Drive.
          </Trans>
        </Text>
        <TextoUltimaCopia dias={dias} />
        <Text>
          <Trans>
            To restore it: Switch file → Import file → Budget file (.zip).
          </Trans>
        </Text>
        <Text>
          <Trans>
            The file contains all your transactions: keep it somewhere private.
          </Trans>
        </Text>
      </View>
    </Setting>
  );
}
